import type {
  InvoiceItemFulfillment,
  KitchenBoardPlate,
  KitchenStation,
  KitchenWorkflow,
} from "@/lib/api/types";
import { handOverStates, printStartsFood } from "./workflow";

/**
 * «حسب الطلب» — THE ORDER VIEW (T13, T5; D170).
 *
 * The expediter reads ORDERS, not stations: one card per bill (`#N`), one row
 * per station with that station's state in words, «مكتمل» only when nothing
 * in the order is queued or cooking, and ONE hand-over for the order.
 *
 * Each station row carries its WORST state (the least advanced plate): a
 * station is only "ready" when all of its plates are. The hand-over serves
 * `handOverStates(workflow)` — READY where the screen records ready, every
 * started plate in a paper workflow (plan §3.1) — as one `markServed` per
 * bill. Pure.
 */

export type OrderStationState = "QUEUED" | "PREPARING" | "READY";

export type OrderStationRow = {
  /** `null` = plates with no station (the store's fallback). */
  stationId: string | null;
  state: OrderStationState;
  plates: KitchenBoardPlate[];
  /** Paper workflow, a plate still QUEUED: its ticket has not printed. */
  unprinted: boolean;
};

export type OrderCard = {
  invoiceId: string;
  /** The first plate the board sent for this bill — its #, place, channel, identity. */
  first: KitchenBoardPlate;
  plates: KitchenBoardPlate[];
  stations: OrderStationRow[];
  /** Nothing in the order is QUEUED or PREPARING. */
  complete: boolean;
  /** The stations still holding the order up, in row order. */
  waitingOn: (string | null)[];
  /** The plates one hand-over serves now. */
  handOver: KitchenBoardPlate[];
};

const RANK: Record<OrderStationState, number> = { QUEUED: 0, PREPARING: 1, READY: 2 };

function isBoardState(state: InvoiceItemFulfillment): state is OrderStationState {
  return state === "QUEUED" || state === "PREPARING" || state === "READY";
}

export function buildOrderCards(
  plates: readonly KitchenBoardPlate[],
  workflow: KitchenWorkflow,
  stations: readonly KitchenStation[] = [],
): OrderCard[] {
  const order = new Map(stations.map((station) => [station.id, station.sortOrder]));
  const handOver = new Set<InvoiceItemFulfillment>(handOverStates(workflow));
  const paper = printStartsFood(workflow);
  const cards = new Map<string, OrderCard>();
  // The server sends plates in first-fire order (T9); a bill's card sits
  // where its first plate did, so the oldest order stays on top.
  for (const plate of plates) {
    if (!isBoardState(plate.fulfillmentState)) continue;
    let card = cards.get(plate.invoiceId);
    if (!card) {
      card = {
        invoiceId: plate.invoiceId,
        first: plate,
        plates: [],
        stations: [],
        complete: true,
        waitingOn: [],
        handOver: [],
      };
      cards.set(plate.invoiceId, card);
    }
    card.plates.push(plate);
    const state = plate.fulfillmentState;
    let row = card.stations.find((one) => one.stationId === plate.stationId);
    if (!row) {
      row = { stationId: plate.stationId, state, plates: [], unprinted: false };
      card.stations.push(row);
    }
    row.plates.push(plate);
    if (RANK[state] < RANK[row.state]) row.state = state;
    if (paper && state === "QUEUED") row.unprinted = true;
    if (handOver.has(state)) card.handOver.push(plate);
  }
  for (const card of cards.values()) {
    card.stations.sort(
      (a, b) =>
        (a.stationId === null ? Number.MAX_SAFE_INTEGER : (order.get(a.stationId) ?? Number.MAX_SAFE_INTEGER - 1)) -
        (b.stationId === null ? Number.MAX_SAFE_INTEGER : (order.get(b.stationId) ?? Number.MAX_SAFE_INTEGER - 1)),
    );
    card.waitingOn = card.stations.filter((row) => row.state !== "READY").map((row) => row.stationId);
    card.complete = card.waitingOn.length === 0;
  }
  return [...cards.values()];
}
